import React, { useCallback, useEffect, useState } from 'react';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import CircularProgress from '@mui/material/CircularProgress';
import Typography from '@mui/material/Typography';
import SendIcon from '@mui/icons-material/Send';
import CheckIcon from '@mui/icons-material/Check';
import agent from '../../agent';
import { DEFAULT_THEME_COLOR } from '../../constants/theme';
import { freaksErrorCode, freaksErrorKey } from '../../utils/theFreaks';
import useFreaksT from './useFreaksT';
import FreaksAvatar from './FreaksAvatar';
import { actionSx, freaksSx as s } from './freaksStyles';

// Invite friends (we follow each other) to the game: a push to each friend, once per friend per day.
export default function FreaksFriends({ notify }) {
  const { t } = useFreaksT();
  const [friends, setFriends] = useState(null);
  const [sending, setSending] = useState(null);

  const load = useCallback(async () => {
    setFriends(null);
    try {
      setFriends(await agent.TheFreaks.friends());
    } catch {
      setFriends([]);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const markInvited = (username) =>
    setFriends((list) => (list ?? []).map((friend) => (friend.username === username ? { ...friend, invitedToday: true } : friend)));

  const invite = async (friend) => {
    setSending(friend.username);
    try {
      await agent.TheFreaks.invite(friend.username);
      markInvited(friend.username);
      notify({ type: 'success', text1: t('theFreaks.friends.invited', { username: friend.username }) });
    } catch (error) {
      if (freaksErrorCode(error) === 'already_invited') markInvited(friend.username);
      notify({ type: 'error', text1: t(freaksErrorKey(error)) });
    } finally {
      setSending(null);
    }
  };

  return (
    <Box data-testid="freaks-friends">
      <Typography sx={s.intro}>{t('theFreaks.friends.intro')}</Typography>
      {friends === null ? <Box sx={{ textAlign: 'center', mt: 2 }}><CircularProgress sx={{ color: DEFAULT_THEME_COLOR }}/></Box> : null}
      {friends && !friends.length ? <Typography sx={s.empty}>{t('theFreaks.friends.empty')}</Typography> : null}
      {(friends ?? []).map((friend) => (
        <Box key={friend.username} sx={{ ...s.card, ...s.between }}>
          <FreaksAvatar username={friend.username} imageUrl={friend.imageUrl}/>
          <Box sx={s.grow}>
            <Typography noWrap sx={s.bold}>@{friend.username}</Typography>
            <Typography sx={{ ...s.small, ...s.muted }}>
              {friend.playing ? `🎮 ${t('theFreaks.friends.playing')}` : t('theFreaks.friends.notPlaying')}
            </Typography>
          </Box>
          <Button
            data-testid={`freaks-invite-${friend.username}`}
            startIcon={sending === friend.username
              ? <CircularProgress size={16} sx={{ color: 'inherit' }}/>
              : friend.invitedToday ? <CheckIcon/> : <SendIcon/>}
            disabled={friend.invitedToday || !!sending}
            onClick={() => invite(friend)}
            sx={actionSx()}
          >
            {t(friend.invitedToday ? 'theFreaks.friends.invitedToday' : 'theFreaks.friends.invite')}
          </Button>
        </Box>
      ))}
      <Typography sx={{ ...s.small, ...s.muted, textAlign: 'center' }}>{t('theFreaks.friends.limit')}</Typography>
    </Box>
  );
}
